/**
 * API Client for DailyVents API
 * Centralized middleware for all API requests with interceptors and error handling
 */

export interface ApiRequestConfig extends RequestInit {
    skipErrorHandling?: boolean
    recaptchaToken?: string
    recaptchaAction?: string
}

export interface ApiResponse<T = any> {
    data: T
    status: number
    statusText: string
    headers: Headers
}

export interface ApiError {
    message: string
    status?: number
    statusText?: string
    data?: any
}

// Request interceptor type
type RequestInterceptor = (
    config: RequestInit,
    url: string
) => RequestInit | Promise<RequestInit>

// Response interceptor type
type ResponseInterceptor = (
    response: Response
) => Response | Promise<Response>

// Error interceptor type
type ErrorInterceptor = (error: ApiError) => void | Promise<void>

// ===== Functional API client implementation =====

const BASE_URL = process.env.REACT_APP_API_BASE_URL || ''

if (!BASE_URL) {
    // eslint-disable-next-line no-console
    console.warn('REACT_APP_API_BASE_URL is not set. API calls may fail.')
}

let requestInterceptors: RequestInterceptor[] = []
let responseInterceptors: ResponseInterceptor[] = []
let errorInterceptors: ErrorInterceptor[] = []

const buildURL = (endpoint: string): string => {
    if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
        return endpoint
    }

    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint
    const cleanBaseURL = BASE_URL.endsWith('/')
        ? BASE_URL.slice(0, -1)
        : BASE_URL

    return `${cleanBaseURL}/${cleanEndpoint}`
}

const getDefaultHeaders = (): HeadersInit => ({
    'Content-Type': 'application/json',
})

const applyRequestInterceptors = async (
    config: RequestInit,
    url: string
): Promise<RequestInit> => {
    let finalConfig = { ...config }

    for (const interceptor of requestInterceptors) {
        finalConfig = await interceptor(finalConfig, url)
    }

    return finalConfig
}

const applyResponseInterceptors = async (
    response: Response
): Promise<Response> => {
    let finalResponse = response

    for (const interceptor of responseInterceptors) {
        finalResponse = await interceptor(finalResponse)
    }

    return finalResponse
}

const applyErrorInterceptors = async (error: ApiError): Promise<void> => {
    for (const interceptor of errorInterceptors) {
        await interceptor(error)
    }
}

const parseResponse = async <T>(response: Response): Promise<T> => {
    const contentType = response.headers.get('content-type')

    if (contentType && contentType.includes('application/json')) {
        return await response.json()
    }

    const text = await response.text()
    try {
        return JSON.parse(text) as T
    } catch {
        return text as unknown as T
    }
}

const handleError = async (
    response: Response,
    skipErrorHandling: boolean = false
): Promise<ApiError> => {
    let errorData: any = null

    try {
        errorData = await parseResponse(response)
    } catch {
        errorData = { message: response.statusText }
    }

    const error: ApiError = {
        message:
            errorData?.message ||
            errorData?.error ||
            `Request failed with status ${response.status}`,
        status: response.status,
        statusText: response.statusText,
        data: errorData,
    }

    if (!skipErrorHandling) {
        await applyErrorInterceptors(error)
    }

    return error
}

const request = async <T = any>(
    endpoint: string,
    config: ApiRequestConfig = {}
): Promise<ApiResponse<T>> => {
    const {
        skipErrorHandling = false,
        headers = {},
        recaptchaToken,
        recaptchaAction,
        method,
        ...restConfig
    } = config

    const url = buildURL(endpoint)

    const defaultHeaders = getDefaultHeaders()

    let headersObj: Record<string, string> = {}

    if (headers instanceof Headers) {
        headers.forEach((value, key) => {
            headersObj[key] = value
        })
    } else if (headers && typeof headers === 'object') {
        headersObj = { ...headers } as Record<string, string>
    }

    const mergedHeaders: Record<string, string> = {
        ...(defaultHeaders as Record<string, string>),
        ...headersObj,
    }

    if (method === 'POST' && recaptchaToken && recaptchaAction) {
        mergedHeaders['X-Recaptcha-Token'] = recaptchaToken
        mergedHeaders['X-Recaptcha-Action'] = recaptchaAction
    }

    let requestConfig: RequestInit = {
        ...restConfig,
        method,
        headers: mergedHeaders,
    }

    requestConfig = await applyRequestInterceptors(requestConfig, url)

    try {
        let response = await fetch(url, requestConfig)

        response = await applyResponseInterceptors(response)

        if (!response.ok) {
            const error = await handleError(response, skipErrorHandling)
            throw error
        }

        const data = await parseResponse<T>(response)

        return {
            data,
            status: response.status,
            statusText: response.statusText,
            headers: response.headers,
        }
    } catch (error) {
        if (error instanceof TypeError && error.message === 'Failed to fetch') {
            const apiError: ApiError = {
                message: 'Network error. Please check your connection.',
                status: 0,
            }

            if (!skipErrorHandling) {
                await applyErrorInterceptors(apiError)
            }

            throw apiError
        }

        if (
            error &&
            typeof error === 'object' &&
            'message' in error &&
            'status' in error
        ) {
            throw error
        }

        const apiError: ApiError = {
            message:
                error instanceof Error
                    ? error.message
                    : 'An unexpected error occurred',
        }

        if (!skipErrorHandling) {
            await applyErrorInterceptors(apiError)
        }

        throw apiError
    }
}

const get = async <T = any>(
    endpoint: string,
    config?: ApiRequestConfig
): Promise<ApiResponse<T>> => {
    return request<T>(endpoint, {
        ...config,
        method: 'GET',
    })
}

const post = async <T = any>(
    endpoint: string,
    data?: any,
    config?: ApiRequestConfig
): Promise<ApiResponse<T>> => {
    return request<T>(endpoint, {
        ...config,
        method: 'POST',
        body: data ? JSON.stringify(data) : undefined,
    })
}

// Functional API surface
export const apiClient = {
    get,
    post,
}

export default apiClient
